import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { CircleDollarSign, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiCallError } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/ui/data-table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle, EmptyDescription } from '@/components/ui/empty';

interface ReceivableRow {
  id: string;
  invoiceNumber: string;
  clientName: string;
  clientRuc: string | null;
  grandTotal: number;
  paid: number;
  saldo: number;
  estado: 'pendiente' | 'parcial' | 'cobrada';
  createdAt: string;
}

const gs = (n: number) => `Gs ${Math.round(n || 0).toLocaleString('es-PY')}`;
const errMsg = (e: unknown, fallback: string) =>
  e instanceof ApiCallError ? (e.body.message ?? e.body.error) : fallback;

function EstadoBadge({ estado }: { estado: ReceivableRow['estado'] }) {
  if (estado === 'cobrada') return <Badge variant="success">Cobrada</Badge>;
  if (estado === 'parcial') return <Badge variant="warning">Parcial</Badge>;
  return <Badge variant="secondary">Pendiente</Badge>;
}

export function ReceivablesTab() {
  const [payRow, setPayRow] = useState<ReceivableRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['app-receivables'],
    queryFn: () => api<{ items: ReceivableRow[]; totalSaldo: number }>('/api/app/receivables'),
  });

  const items = data?.items ?? [];

  const columns = useMemo<ColumnDef<ReceivableRow, unknown>[]>(
    () => [
      {
        accessorKey: 'invoiceNumber',
        header: 'Factura',
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.invoiceNumber}</span>,
      },
      {
        id: 'cliente',
        header: 'Cliente',
        accessorFn: (r) => `${r.clientName} ${r.clientRuc ?? ''}`,
        cell: ({ row }) => (
          <div>
            <div className="max-w-[14rem] truncate leading-tight">{row.original.clientName}</div>
            {row.original.clientRuc && (
              <div className="font-mono text-xs text-muted-foreground">{row.original.clientRuc}</div>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Fecha',
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-sm">
            {format(new Date(row.original.createdAt), 'dd MMM yyyy', { locale: es })}
          </span>
        ),
      },
      {
        accessorKey: 'grandTotal',
        header: 'Total',
        cell: ({ row }) => <span className="tabular-nums">{gs(row.original.grandTotal)}</span>,
      },
      {
        accessorKey: 'paid',
        header: 'Cobrado',
        cell: ({ row }) => <span className="tabular-nums">{gs(row.original.paid)}</span>,
      },
      {
        accessorKey: 'saldo',
        header: 'Saldo',
        cell: ({ row }) => <span className="font-semibold tabular-nums">{gs(row.original.saldo)}</span>,
      },
      {
        accessorKey: 'estado',
        header: 'Estado',
        cell: ({ row }) => <EstadoBadge estado={row.original.estado} />,
      },
      {
        id: 'accion',
        header: '',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.estado !== 'cobrada' ? (
            <Button variant="outline" size="sm" onClick={() => setPayRow(row.original)}>
              Registrar pago
            </Button>
          ) : null,
      },
    ],
    []
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="flex items-center gap-3">
          <div className="flex size-9 items-center justify-center rounded-lg bg-muted">
            <Wallet className="size-5 text-muted-foreground" />
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Total por cobrar</div>
            <div className="text-2xl font-bold tabular-nums">{gs(data?.totalSaldo ?? 0)}</div>
          </div>
        </CardContent>
      </Card>

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      )}

      {!isLoading && items.length === 0 && (
        <Card>
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <CircleDollarSign />
              </EmptyMedia>
              <EmptyTitle>Sin cuentas por cobrar</EmptyTitle>
              <EmptyDescription>
                Las facturas electrónicas que emitís <strong>a crédito</strong> aparecen acá hasta que
                las cobrás.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        </Card>
      )}

      {!isLoading && items.length > 0 && (
        <DataTable
          columns={columns}
          data={items}
          searchPlaceholder="Buscar por factura o cliente…"
          exportFileName="cuentas-por-cobrar"
          exportRows={(rows) =>
            rows.map((r) => ({
              Factura: r.invoiceNumber,
              Cliente: r.clientName,
              RUC: r.clientRuc ?? '',
              Fecha: format(new Date(r.createdAt), 'dd/MM/yyyy'),
              Total: r.grandTotal,
              Cobrado: r.paid,
              Saldo: r.saldo,
              Estado: r.estado,
            }))
          }
          onRowClick={(r) => r.estado !== 'cobrada' && setPayRow(r)}
        />
      )}

      <PaymentDialog row={payRow} onClose={() => setPayRow(null)} />
    </div>
  );
}

function PaymentDialog({ row, onClose }: { row: ReceivableRow | null; onClose: () => void }) {
  const [amount, setAmount] = useState('');
  const [paidAt, setPaidAt] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [method, setMethod] = useState('Transferencia');
  const [notes, setNotes] = useState('');
  const qc = useQueryClient();

  const pay = useMutation({
    mutationFn: () =>
      api(`/api/app/electronic-invoices/${row!.id}/payments`, {
        method: 'POST',
        body: JSON.stringify({
          amount: Number(amount.replace(/[^\d]/g, '')) || 0,
          paidAt,
          method: method || null,
          notes: notes.trim() || null,
        }),
      }),
    onSuccess: () => {
      toast.success('Pago registrado.');
      qc.invalidateQueries({ queryKey: ['app-receivables'] });
      setAmount('');
      setNotes('');
      onClose();
    },
    onError: (e) => toast.error(errMsg(e, 'No se pudo registrar el pago')),
  });

  const amountNum = Number(amount.replace(/[^\d]/g, '')) || 0;
  const overpay = !!row && amountNum > row.saldo;
  const valid = amountNum >= 1 && !!paidAt;

  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Registrar pago</DialogTitle>
          <DialogDescription>
            {row ? `${row.invoiceNumber} · ${row.clientName} · saldo ${gs(row.saldo)}` : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="pay-amount">Monto (Gs)</Label>
            <Input
              id="pay-amount"
              inputMode="numeric"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={row ? String(row.saldo) : '0'}
            />
            {row && (
              <button
                type="button"
                className="text-xs text-primary hover:underline"
                onClick={() => setAmount(String(row.saldo))}
              >
                Pagar el saldo completo ({gs(row.saldo)})
              </button>
            )}
            {overpay && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                El monto supera el saldo — quedará como cobrada.
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Fecha</Label>
              <DatePicker value={paidAt} onChange={setPaidAt} />
            </div>
            <div className="space-y-1">
              <Label>Método</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Transferencia">Transferencia</SelectItem>
                  <SelectItem value="Efectivo">Efectivo</SelectItem>
                  <SelectItem value="Cheque">Cheque</SelectItem>
                  <SelectItem value="Otro">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="pay-notes">Nota (opcional)</Label>
            <Input id="pay-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button disabled={!valid || pay.isPending} onClick={() => pay.mutate()}>
            Registrar pago
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
